import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { LockClosedIcon, PhotoIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { Button } from 'src/components/button'
import { Spinner } from 'src/components/spinner'
import { usePermissions } from 'src/hooks/use-permissions'
import { getSurveyReview } from 'src/services/reservation-guests/api'
import { ReservationGuest, SurveySnapshot } from 'src/services/reservation-guests'
import { getPresignedDownloadUrl } from 'src/services/survey/api'

const REVIEW_PERMISSION = 'reservations.access'

const SNAPSHOT_ROWS: Array<[keyof SurveySnapshot, string]> = [
  ['documentType', 'docType'],
  ['documentNumber', 'docNumber'],
  ['dateOfBirth', 'dob'],
  ['nationality', 'nationality'],
  ['visitingFrom', 'visitingFrom'],
  ['residenceCity', 'residence'],
]

const documentLabelKey = (exportKey: string) => exportKey.replace(/_(en|es)$/, '')

/**
 * The survey content of a guest card. Permitted staff load it from the audited
 * review endpoint (no PIN prompt); everyone else sees a locked state and no values.
 * Document files open through a presigned download requested on click, only after
 * the content has loaded. Nothing is written to web storage or the URL.
 */
export const GuestSurveyReview = ({ guest }: { guest: ReservationGuest }) => {
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const permitted = hasPermission(REVIEW_PERMISSION) && Boolean(guest.formInstance)

  const {
    data: review,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['guest-survey-review', guest.id],
    queryFn: () => getSurveyReview({ reservationGuestId: guest.id }),
    enabled: permitted,
    retry: false,
    gcTime: 0,
  })

  const snapshot = review?.surveySnapshot ?? guest.surveySnapshot
  const documents = review?.documents ?? []

  // Documents whose file could not be opened (e.g. storage not configured in dev).
  const [unavailable, setUnavailable] = useState<string[]>([])

  const openDocument = async (exportKey: string, objectKey: string) => {
    if (!guest.formInstance) return
    try {
      const { downloadUrl } = await getPresignedDownloadUrl({
        objectKey,
        instance: guest.formInstance,
      })
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch {
      setUnavailable((current) => [...current, exportKey])
    }
  }

  if (permitted && isLoading) {
    return <Spinner size="sm" />
  }

  if (!snapshot && !permitted && guest.formInstance) {
    return (
      <div
        className="flex items-start gap-2 rounded-md bg-surface-hover px-3 py-2 text-xs text-text-secondary"
        data-testid="survey-locked"
      >
        <LockClosedIcon className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span>{t('reservationGuestCard.surveyLocked')}</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        {SNAPSHOT_ROWS.map(([field, labelKey]) =>
          snapshot?.[field] ? (
            <div key={field} className="contents">
              <span className="text-text-secondary">{t(`reservationGuestCard.${labelKey}`)}</span>
              <span className="text-text-primary">{snapshot[field]}</span>
            </div>
          ) : null,
        )}
        {!snapshot && (
          <span className="col-span-2 text-text-placeholder">
            {t('reservationGuestCard.noSurveyData')}
          </span>
        )}
      </div>
      {isError && (
        <span className="text-xs text-error" data-testid="survey-review-error">
          {t('reservationGuestCard.surveyReviewError')}
        </span>
      )}
      {documents.length > 0 && (
        <div className="flex flex-col items-start gap-1" data-testid="survey-documents">
          {documents.map((doc) => (
            <div key={doc.exportKey} className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                icon={<PhotoIcon className="h-4 w-4" aria-hidden="true" />}
                onClick={() => openDocument(doc.exportKey, doc.objectKey)}
                data-testid={`survey-document-${documentLabelKey(doc.exportKey)}`}
              >
                {t(`reservationGuestCard.documents.${documentLabelKey(doc.exportKey)}`)}
              </Button>
              {unavailable.includes(doc.exportKey) && (
                <span
                  className="text-xs text-text-secondary"
                  data-testid={`survey-document-unavailable-${documentLabelKey(doc.exportKey)}`}
                >
                  {t('reservationGuestCard.documentUnavailable')}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
